/**
 * System health page helpers.
 *
 * @version 2026-04-22-v1
 */
import type { HealthIntegration } from '../../../lib/types';

export function healthStatusTone(status: HealthIntegration['status']) {
    switch (status) {
        case 'healthy':
            return 'success';
        case 'elevated':
            return 'info';
        case 'degraded':
            return 'warning';
        case 'outage':
            return 'danger';
        case 'unavailable':
            return 'muted';
        default:
            return 'muted';
    }
}

export function healthStatusLabel(status: HealthIntegration['status']) {
    switch (status) {
        case 'healthy':
            return 'Healthy';
        case 'elevated':
            return 'Elevated errors';
        case 'degraded':
            return 'Degraded';
        case 'outage':
            return 'Outage';
        case 'unavailable':
            return 'Unavailable';
        default:
            return 'Unavailable';
    }
}

export function healthPulseStatus(status: HealthIntegration['status']) {
    switch (status) {
        case 'healthy':
            return 'up';
        case 'elevated':
        case 'degraded':
            return 'degraded';
        case 'outage':
            return 'down';
        case 'unavailable':
        default:
            return 'unknown';
    }
}

export function isUnavailableIntegration(integration: HealthIntegration) {
    return integration.status === 'unavailable' || integration.source_state === 'unavailable';
}

export function formatMetric(value: number | null | undefined, digits = 0) {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return numeric.toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
    });
}

export function formatPercent(value: number | null | undefined) {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return `${numeric.toFixed(2)}%`;
}

export function formatLatency(value: number | null | undefined) {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return `${Math.round(numeric)}ms`;
}

export function getHealthSourceNote(integration: HealthIntegration) {
    if (integration.source_note) return integration.source_note;
    if (isUnavailableIntegration(integration)) {
        return 'No live telemetry source is wired for this signal yet.';
    }
    return '';
}
